// Global mandatory-acknowledgement banner for Important announcements.
// Mounts once at app root. On every login (and periodically) it fetches
// `/api/announcements/mandatory`. If any items are unacknowledged and
// the user is in the audience, a full-screen banner is shown.
//
// Rules:
//   • X (close) button dismisses for this session — will reappear next login.
//   • After 5 unacknowledged logins the server returns `force_ack: true`;
//     the close (X) button is hidden and the "I Acknowledge" button is
//     disabled for `force_countdown_seconds` (default 60) — a live timer
//     is shown so the user is forced to read.
//   • Multiple pending announcements are shown one at a time.
//
// Data-testid coverage:
//   mandatory-banner-root | -title | -body | -ack-btn | -close-btn | -timer

import { useEffect, useState, useRef, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { fmtDateTimeIST } from "../lib/datetime";
import { toast } from "sonner";

export default function MandatoryAnnouncementBanner() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);        // list of mandatory items
  const [current, setCurrent] = useState(null);  // active item shown
  const [secondsLeft, setSecondsLeft] = useState(0);
  const seenCalledRef = useRef(new Set());       // ids for which we've called /seen
  const dismissedRef = useRef(new Set());        // ids the user X-closed this session

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get("/announcements/mandatory");
      setQueue(Array.isArray(data) ? data : []);
    } catch {
      // silent — user may not have permission etc.
    }
  }, [user?.id]);

  // Load on mount + when user changes
  useEffect(() => { load(); }, [load]);

  // Also refetch every 2 minutes so a newly-approved important post appears live
  useEffect(() => {
    if (!user?.id) return;
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [user?.id, load]);

  // Pick the next visible item from queue
  useEffect(() => {
    if (current) return;
    const next = queue.find((q) => !dismissedRef.current.has(q.id));
    setCurrent(next || null);
  }, [queue, current]);

  // Count seen once per session per id, and start countdown if force_ack
  useEffect(() => {
    if (!current) { setSecondsLeft(0); return; }
    if (!seenCalledRef.current.has(current.id)) {
      seenCalledRef.current.add(current.id);
      api.post(`/announcements/${current.id}/seen`).catch(() => {});
    }
    if (current.force_ack) {
      setSecondsLeft(current.force_countdown_seconds || 60);
    } else {
      setSecondsLeft(0);
    }
  }, [current]);

  // Countdown ticker
  useEffect(() => {
    if (!current?.force_ack || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [current, secondsLeft]);

  const [busy, setBusy] = useState(false);

  if (!current) return null;

  const acknowledge = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/announcements/${current.id}/acknowledge`);
      console.log("[MandatoryAnnouncementBanner] ack ok:", res.status, res.data);
      toast.success("Acknowledged");
      setQueue((q) => q.filter((x) => x.id !== current.id));
      setCurrent(null);
    } catch (e) {
      console.error("[MandatoryAnnouncementBanner] ack failed:", e?.response?.status, e?.response?.data, e);
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? `Acknowledge failed: ${detail}`
          : e?.message
          ? `Acknowledge failed: ${e.message}`
          : "Failed to acknowledge — please try again",
      );
    } finally {
      setBusy(false);
    }
  };

  const closeForSession = () => {
    if (current.force_ack) return; // shouldn't be reachable, close is hidden
    dismissedRef.current.add(current.id);
    setCurrent(null);
  };

  const disableAck = current.force_ack && secondsLeft > 0;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="mandatory-banner-root"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-2xl w-full rounded-2xl bg-white shadow-2xl border-2 border-rose-400 overflow-hidden">
        <div className="flex items-center justify-between bg-gradient-to-r from-rose-600 to-red-500 text-white px-5 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <div className="text-[13.5px] font-semibold tracking-wide uppercase">
              Mandatory To Read
            </div>
          </div>
          {!current.force_ack && (
            <button
              onClick={closeForSession}
              className="opacity-80 hover:opacity-100"
              aria-label="Dismiss for now"
              data-testid="mandatory-banner-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="text-xl font-semibold text-slate-900" data-testid="mandatory-banner-title">
            {current.title}
          </div>
          <div className="text-[12.5px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
            <span>By <strong>{current.created_by_name}</strong></span>
            {current.created_by_designation && <span>· {current.created_by_designation}</span>}
            <span>· Posted {fmtDateTimeIST(current.published_at || current.created_at)}</span>
            <span>· Ends {fmtDateTimeIST(current.end_at)}</span>
          </div>
          <div
            className="rounded-xl bg-slate-50 border border-slate-200 p-4 whitespace-pre-wrap text-[14px] text-slate-800"
            data-testid="mandatory-banner-body"
          >
            {current.body}
          </div>
          {current.external_url && (
            <a
              href={current.external_url}
              target="_blank"
              rel="noreferrer"
              className="text-emerald-700 hover:underline text-[13px]"
            >
              {current.external_url}
            </a>
          )}
          {current.force_ack && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-[12.5px]">
              You have skipped this announcement for {current.seen_count || 5}+ logins. Please read it carefully — the button will enable in <strong data-testid="mandatory-banner-timer">{secondsLeft}s</strong>.
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-4 flex items-center justify-end gap-2 bg-slate-50/60">
          <Button
            onClick={acknowledge}
            disabled={disableAck || busy}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
            data-testid="mandatory-banner-ack-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {busy ? "Acknowledging…" : disableAck ? `I Acknowledge (${secondsLeft}s)` : "I Acknowledge"}
          </Button>
        </div>
      </div>
    </div>
  );
}
